import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, LogOut, Settings, User } from "lucide-react";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { logOut } from "../../../../features/auth/authSlice";
import { useSendLogoutMutation } from "../../../../features/auth/authApiSlice";
import { useAuth } from "../../../../hooks/useAuth";
import { useToast } from "../../../../hooks/useToast";
import { getInitials, resolveImageUrl } from "./headerUtils";

interface HeaderUserMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const HeaderUserMenu: React.FC<HeaderUserMenuProps> = ({
  isOpen,
  onToggle,
  onClose,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const toast = useToast();
  const [sendLogout] = useSendLogoutMutation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const settingsPath = location.pathname.startsWith("/super-admin")
    ? "/super-admin/settings"
    : "/admin/settings";
  const imageUrl = resolveImageUrl(user?.imageUrl);
  const initials = getInitials(user?.fullName);

  const goTo = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await sendLogout({}).unwrap();
    } catch {
      dispatch(logOut());
    } finally {
      toast.success({
        title: "Signed out",
        message: "You have been signed out successfully.",
      });
      navigate("/sign-in", { replace: true });
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-label="Account menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="flex items-center gap-2 rounded-xl border border-slate-200 py-1 pl-1 pr-2 transition-colors hover:bg-slate-50"
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={user?.fullName || "Account"}
            className="h-8 w-8 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-xs font-semibold text-white">
            {initials}
          </div>
        )}
        <span className="hidden max-w-[120px] truncate text-sm font-medium text-slate-700 lg:block">
          {user?.fullName}
        </span>
        <ChevronDown
          size={16}
          className={`hidden sm:block text-pColor transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-[calc(100%+8px)] z-50 w-[260px] rounded-2xl border border-gray-100 bg-white shadow-xl overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-hColor truncate">
                {user?.fullName || "Account"}
              </p>
              <p className="text-xs text-pColor truncate">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={() => goTo(`${settingsPath}?tab=profile`)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-hColor hover:bg-gray-50"
            >
              <User size={16} className="text-pColor" />
              Profile
            </button>
            <button
              type="button"
              onClick={() => goTo(settingsPath)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-hColor hover:bg-gray-50"
            >
              <Settings size={16} className="text-pColor" />
              Settings
            </button>
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <LogOut size={16} />
              {isLoggingOut ? "Signing out..." : "Sign out"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeaderUserMenu;
